import React, { useState } from 'react'
import { IoWarningOutline, IoTrendingUp, IoTrendingDown } from 'react-icons/io5'
import type { RefereeProfileHub } from '../../../types'
import { MinuteBandChart } from '../components/MinuteBandChart'
import { Sparkline } from '../../Sparkline'

interface RefereePanelProps {
  referee: RefereeProfileHub | null | undefined
}

type DataWindow = 'season' | 'last_5' | 'last_10' | 'last_20'

export function RefereePanel({ referee }: RefereePanelProps) {
  const [dataWindow, setDataWindow] = useState<DataWindow>('season')

  if (!referee) {
    return (
      <div className="cards-panel cards-panel-empty">
        <IoWarningOutline size={32} />
        <h3>Referee Not Assigned</h3>
        <p>Referee data will be available once the match official is confirmed.</p>
      </div>
    )
  }

  if (!referee.assigned) {
    return (
      <div className="cards-panel cards-panel-empty">
        <IoWarningOutline size={32} />
        <h3>Referee TBD</h3>
        <p>The referee for this match has not been assigned yet. Check back closer to kickoff.</p>
      </div>
    )
  }

  // Get stats for selected window
  const getWindowStats = () => {
    if (dataWindow === 'season') {
      return {
        yellows: referee.yellows_per_match,
        reds: referee.reds_per_match,
        foulsPerCard: referee.fouls_per_card,
      }
    }
    const rolling = referee.rolling?.[dataWindow]
    if (rolling) {
      return {
        yellows: rolling.yellows_per_match,
        reds: rolling.reds_per_match ?? referee.reds_per_match,
        foulsPerCard: rolling.fouls_per_card ?? referee.fouls_per_card,
      }
    }
    return {
      yellows: referee.yellows_per_match,
      reds: referee.reds_per_match,
      foulsPerCard: referee.fouls_per_card,
    }
  }

  const stats = getWindowStats()

  // Determine strictness color
  const getStrictnessColor = () => {
    if (referee.league_percentile) {
      if (referee.league_percentile >= 75) return 'var(--color-danger)'
      if (referee.league_percentile >= 50) return 'var(--color-warning)'
      return 'var(--color-success)'
    }
    return 'var(--color-text-muted)'
  }

  return (
    <div className="cards-panel referee-panel">
      {/* Referee Header */}
      <div className="referee-panel-header">
        <div className="referee-info">
          <h3 className="referee-name">{referee.name}</h3>
          <span className="referee-matches">{referee.matches_sampled} matches this season</span>
        </div>
        {referee.league_rank && referee.league_total_refs && (
          <div className="referee-ranking" style={{ color: getStrictnessColor() }}>
            <span className="referee-rank-value">#{referee.league_rank}</span>
            <span className="referee-rank-label">of {referee.league_total_refs} refs</span>
          </div>
        )}
      </div>

      {/* Sample Size Warning */}
      {referee.matches_sampled < 5 && (
        <div className="cards-warning-banner">
          <IoWarningOutline size={16} />
          <span>Small sample size ({referee.matches_sampled} matches) - interpret with caution</span>
        </div>
      )}

      {/* Data Window Selector */}
      <div className="cards-window-selector">
        <button
          type="button"
          className={`chip ${dataWindow === 'season' ? 'chip-active' : ''}`}
          onClick={() => setDataWindow('season')}
        >
          Season
        </button>
        {referee.rolling?.last_5 && (
          <button
            type="button"
            className={`chip ${dataWindow === 'last_5' ? 'chip-active' : ''}`}
            onClick={() => setDataWindow('last_5')}
          >
            Last 5
          </button>
        )}
        {referee.rolling?.last_10 && (
          <button
            type="button"
            className={`chip ${dataWindow === 'last_10' ? 'chip-active' : ''}`}
            onClick={() => setDataWindow('last_10')}
          >
            Last 10
          </button>
        )}
        {referee.rolling?.last_20 && (
          <button
            type="button"
            className={`chip ${dataWindow === 'last_20' ? 'chip-active' : ''}`}
            onClick={() => setDataWindow('last_20')}
          >
            Last 20
          </button>
        )}
      </div>

      {/* Main Stats Grid */}
      <div className="referee-stats-grid">
        <div className="referee-stat-card">
          <span className="referee-stat-value">{stats.yellows.toFixed(1)}</span>
          <span className="referee-stat-label">Yellows/Match</span>
          {referee.yellows_vs_league_avg && (
            <span
              className={`referee-stat-diff ${referee.yellows_vs_league_avg > 1 ? 'up' : referee.yellows_vs_league_avg < 1 ? 'down' : ''}`}
            >
              {referee.yellows_vs_league_avg > 1 ? <IoTrendingUp size={12} /> : <IoTrendingDown size={12} />}
              {referee.yellows_vs_league_avg > 1 ? '+' : ''}{((referee.yellows_vs_league_avg - 1) * 100).toFixed(0)}% vs avg
            </span>
          )}
        </div>

        <div className="referee-stat-card">
          <span className="referee-stat-value">{stats.reds.toFixed(2)}</span>
          <span className="referee-stat-label">Reds/Match</span>
        </div>

        <div className="referee-stat-card">
          <span className="referee-stat-value">{stats.foulsPerCard.toFixed(1)}</span>
          <span className="referee-stat-label">Fouls/Card</span>
          <span className="referee-stat-hint">{stats.foulsPerCard < 3 ? 'Strict' : stats.foulsPerCard > 4 ? 'Lenient' : 'Average'}</span>
        </div>

        {referee.home_card_pct != null && (
          <div className="referee-stat-card">
            <span className="referee-stat-value">{(referee.home_card_pct * 100).toFixed(0)}%</span>
            <span className="referee-stat-label">To Home Team</span>
            <span className="referee-stat-hint">{referee.home_card_pct > 0.55 ? 'Home bias' : referee.home_card_pct < 0.45 ? 'Away bias' : 'Neutral'}</span>
          </div>
        )}
      </div>

      {/* League Percentile Bar */}
      {referee.league_percentile != null && (
        <div className="referee-percentile-section">
          <div className="referee-percentile-header">
            <span className="referee-percentile-label">League Strictness</span>
            <span className="referee-percentile-value" style={{ color: getStrictnessColor() }}>
              {referee.league_percentile.toFixed(0)}th percentile
            </span>
          </div>
          <div className="referee-percentile-bar">
            <div
              className="referee-percentile-fill"
              style={{
                width: `${referee.league_percentile}%`,
                backgroundColor: getStrictnessColor(),
              }}
            />
          </div>
          <div className="referee-percentile-labels">
            <span>Lenient</span>
            <span>Strict</span>
          </div>
        </div>
      )}

      {/* Minute Band Distribution */}
      {referee.minute_bands && referee.minute_bands.length > 0 && (
        <div className="referee-minute-bands">
          <h4 className="referee-section-title">Card Timing Distribution</h4>
          <MinuteBandChart bands={referee.minute_bands} />
        </div>
      )}

      {/* Team-Specific History */}
      {(referee.vs_home_team || referee.vs_away_team) && (
        <div className="referee-team-history">
          <h4 className="referee-section-title">Referee vs Teams History</h4>
          <div className="referee-team-history-grid">
            {referee.vs_home_team && referee.vs_home_team.matches > 0 && (
              <div className="referee-team-card">
                <span className="referee-team-label">vs Home Team</span>
                <span className="referee-team-stat">
                  {referee.vs_home_team.avg_cards.toFixed(1)} cards/game
                </span>
                <span className="referee-team-sample">({referee.vs_home_team.matches} matches)</span>
                {referee.vs_home_team.yellows_deviation != null && (
                  <span className={`referee-team-deviation ${referee.vs_home_team.yellows_deviation > 0 ? 'up' : 'down'}`}>
                    {referee.vs_home_team.yellows_deviation > 0 ? '+' : ''}
                    {referee.vs_home_team.yellows_deviation.toFixed(1)} vs team avg
                  </span>
                )}
              </div>
            )}
            {referee.vs_away_team && referee.vs_away_team.matches > 0 && (
              <div className="referee-team-card">
                <span className="referee-team-label">vs Away Team</span>
                <span className="referee-team-stat">
                  {referee.vs_away_team.avg_cards.toFixed(1)} cards/game
                </span>
                <span className="referee-team-sample">({referee.vs_away_team.matches} matches)</span>
                {referee.vs_away_team.yellows_deviation != null && (
                  <span className={`referee-team-deviation ${referee.vs_away_team.yellows_deviation > 0 ? 'up' : 'down'}`}>
                    {referee.vs_away_team.yellows_deviation > 0 ? '+' : ''}
                    {referee.vs_away_team.yellows_deviation.toFixed(1)} vs team avg
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confidence Flags */}
      {referee.confidence_flags.length > 0 && (
        <div className="referee-flags">
          {referee.confidence_flags.map((flag, i) => (
            <div key={i} className="cards-warning-banner cards-warning-banner-small">
              <IoWarningOutline size={14} />
              <span>{flag.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
